import React, { useState } from 'react';
import { universityData } from '@/resources/colleges_15';
import { AnalysisData } from '@/types';
import { DraftVersion } from '@/services/draftService';
import { timestampToDate } from '@/utils/common.utils';

interface DraftListProps {
  drafts: DraftVersion[]
  autofillDraft: (draft: AnalysisData) => void;
  universityId: number;
  prompt: string;
}

const DraftList: React.FC<DraftListProps> = ({ drafts, autofillDraft, universityId, prompt }) => {
  const [activeKey, setActiveKey] = useState<number | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<DraftVersion | null>(null);

  const handleChange = (key: number) => {
    setActiveKey(key === activeKey ? null : key);
  };

  const showModal = (draftDetails: DraftVersion) => {
    setSelectedDraft(draftDetails);
    setIsModalVisible(true);
  };

  const handleOk = () => {

    console.log({selectedDraft})
    if (selectedDraft) {
      autofillDraft({
      prompt: selectedDraft.prompt_text,
      wordCount: selectedDraft.word_count,
      promptId: selectedDraft.prompt_id,
      draftId: selectedDraft.draft_id.toString(),
      essay: selectedDraft.essay,
      universityId,
      draftVersionId: selectedDraft.id,
      draftStatus: selectedDraft.draft_status,
      });
    }
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };



  return (
    <div className="draft-list">
      {drafts.length === 0 && prompt ? (
        <div className="text-center text-gray-400 dark:text-gray-500 mt-10">
          <svg className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <div className="text-gray-500 dark:text-gray-400">No matching drafts found for current prompt.</div>
        </div>
      ) : (
        <div className="space-y-4">
          {drafts.map((draft) => {

           

            const draftType = draft.draft_name.startsWith('Supplement') ? 'supplement' : 'common_app';


            const firstFiveWords = draft.essay ? draft.essay.split(/\s+/).slice(0, 5).join(' ') : '';

            return (
              <div key={draft.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex justify-between items-center"
                  onClick={() => handleChange(draft.draft_id)}
                >
                
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{draft.draft_name} - {timestampToDate(draft.created_at)}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{firstFiveWords}...</div>
                  </div>
                  <svg 
                    className={`w-5 h-5 transition-transform text-gray-900 dark:text-gray-100 ${activeKey === draft.draft_id ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {activeKey === draft.draft_id && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                    <div 
                      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      onClick={() => showModal(draft)}
                    >
                      <div className="mb-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          draftType === 'common_app' 
                            ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200' 
                            : 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200'
                        }`}>
                          {draftType === 'common_app' ? 'Common App' : 'Supplement'}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">Prompt:</span>
                          <p className="text-gray-700 dark:text-gray-300 mt-1">{draft.prompt_text}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">Essay:</span>
                          <p className="text-gray-700 dark:text-gray-300 mt-1">{draft.essay}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">Word Count:</span>
                          <span className="ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                            {draft.word_count} words
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">Status:</span>
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                            draft.draft_status === 'Mandatory' 
                              ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200' 
                              : 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200'
                          }`}>
                            {draft.draft_status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal to confirm autofill */}
      {isModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 overflow-y-auto max-h-[620px]">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Complete Draft</h3>
            {selectedDraft && (
              <div className="space-y-3 mb-6">
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">Prompt:</span>
                  <p className="text-gray-700 dark:text-gray-300 mt-1">{selectedDraft.prompt_text}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">Essay:</span>
                  <p className="text-gray-700 dark:text-gray-300 mt-1">{selectedDraft.essay}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">Word Count:</span>
                  <span className="ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                    {selectedDraft.word_count} words
                  </span>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleOk}
                className="flex-1 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
              >
                Use Draft
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftList; 